'use no memo';
import React, { useContext, useEffect, useRef, memo } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { css } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Tooltip from '@atlaskit/tooltip/Tooltip';

// eslint-disable-next-line no-restricted-imports -- deprecated - use RouteState instead
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { createSingleQueryParamHook } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { useEditSourceActions } from '@confluence/edit-common/entry-points/useEditSource.ts';
import {
	CREATE_PAGE_SHORTCUT,
	CREATE_NAV_BUTTON_CLICK,
} from '@confluence/edit-common/entry-points/event-names.ts';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import { useKeyboardShortcutsEnabled } from '@confluence/shortcuts/entry-points/useKeyboardShortcutsEnabled.ts';
import {
	ShortcutVisualizer,
	isMac,
} from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import {
	BLOG_SHORTCUT,
	UNIVERSAL_BLOG_SHORTCUT,
	GLOBAL_CREATE_SHORTCUT,
	UNIVERSAL_GLOBAL_CREATE_SHORTCUT_MAC,
	UNIVERSAL_GLOBAL_CREATE_SHORTCUT_WIN,
	UNIVERSAL_CREATE_LIVE_DOC_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { ConfluencePageSubType } from '@confluence/create-blank-fabric-page/entry-points/CreateBlankPageMutation.ts';
import { UniversalCreate } from '@confluence/create-blank-fabric-page/entry-points/UniversalCreate.ts';
import { buildCreationLink } from '@confluence/create-blank-fabric-page/entry-points/createContentRouteUtils.ts';
import { isCreateBlankFabricPageEnabled } from '@confluence/create-blank-fabric-page/entry-points/helpers.ts';
import { useSpaceEnabledContentTypes } from '@confluence/create-blank-fabric-page/entry-points/useSpaceEnabledContentTypes.ts';
import { START_TOUCH } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { EDITOR_ONBOARDING_STATE_KEYS } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState/onboardingState.ts';
import { preloadEditorOnboardingState } from '@confluence/onboarding-helpers/entry-points/preloadEditorOnboardingState.ts';
import { usePageSpaceKey, usePageContentId } from '@confluence/page-context';
import { CreateContentButtonPreloader } from '@confluence/edit-button/entry-points/CreateContentButtonPreloader.ts';
import { useIsCreationDropdownEnabled } from '@confluence/content-types-utils/entry-points/useIsCreationDropdownEnabled.ts';
import { useHardStorageEnforcement } from '@confluence/storage-enforcement/entry-points/HardEnforcement/useHardStorageEnforcement.ts';
import { useSSRPlaceholderReplaceIdProp, LoadablePaint } from '@confluence/loadable';
import { fg } from '@confluence/feature-gating';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { ConfluenceSmartCreationModalLoader } from '@confluence/smart-create/entry-points/smartCreationModal.tsx';
import { usePreloadSmartCreationModal } from '@confluence/smart-create/entry-points/usePreloadSmartCreationModal.ts';

import { EngagementInlineDialogLoader } from './EngagementInlineDialogLoader.tsx';

const containerStyles = css({
	display: 'flex',
	alignItems: 'center',
});

const CreateButton = LoadablePaint<any>({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-TopNav" */ '@atlaskit/navigation-system/top-nav-items'
			)
		).CreateButton,
});

const LIVE_DOCS_LINK =
	'https://support.atlassian.com/confluence-cloud/docs/create-and-collaborate-in-real-time-with-live-docs/';

// Created outside the component so the hook is not recreated on every render.
const useOpenCreateWithRovoParam = createSingleQueryParamHook('openCreateWithRovo');

const CreateComponentContent = ({ flags }) => {
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const { setQueryParams, match, push } = useContext(RoutesContext);
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isCreationDropdownEnabled = useIsCreationDropdownEnabled();
	const { enforceStorageLimit } = useHardStorageEnforcement({
		source: 'create-component',
	});
	const [areKeyboardShortcutsEnabled] = useKeyboardShortcutsEnabled();
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();

	const isOnEditRoute = useIsEditorPage();
	const { isCreateLiveDocEnabled } = useSpaceEnabledContentTypes(spaceKey);

	const { setEditSource } = useEditSourceActions();
	const { data } = useGetOnboardingState(
		Object.values({
			...EDITOR_ONBOARDING_STATE_KEYS,
		}),
		!fg('cc_onboarding_experience'),
	);

	const { editorOnboardingEligiblity } = deserializeState(data);

	const shouldOpenCreateWithRovo = useOpenCreateWithRovoParam();
	const preloadSmartCreationModal = usePreloadSmartCreationModal();
	const hasPreloadedForQueryParamRef = useRef(false);

	useEffect(() => {
		if (
			hasPreloadedForQueryParamRef.current ||
			(shouldOpenCreateWithRovo !== 'true' && shouldOpenCreateWithRovo !== 'atlassianNew')
		) {
			return;
		}
		hasPreloadedForQueryParamRef.current = true;
		if (preloadSmartCreationModal()) {
			void ConfluenceSmartCreationModalLoader.preload();
		}
	}, [shouldOpenCreateWithRovo, preloadSmartCreationModal]);

	const fireAnalytics = (additionalAttributes = {}) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'globalNavigation',
				action: 'clicked',
				actionSubject: 'navigationItem',
				actionSubjectId: 'create',
				attributes: {
					selectedItemPageContext: match ? match.name : undefined,
					navigationLayer: 'global',
					navVersion: '4',
					navdexPointType: START_TOUCH,
					...additionalAttributes,
				},
			},
		}).fire();
	};

	const openCreateDialog = () => {
		fireAnalytics();
		setQueryParams({ createDialog: true });
	};

	const shouldCreateBlankFabricPage = (): boolean => {
		if (data && editorOnboardingEligiblity) {
			void preloadEditorOnboardingState();
		}

		return true;
	};

	const createPageHref = buildCreationLink({
		contentType: 'page',
		source: 'createBlankFabricPage',
		withFallback: true,
		spaceKey,
		parentPageId: contentId,
	});

	const handleCreatePageShortcut = () => {
		setEditSource(CREATE_PAGE_SHORTCUT);
		fireAnalytics({ keyboardShortcut: true });
		const shouldCreate = shouldCreateBlankFabricPage();

		if (shouldCreate) {
			push(createPageHref);
		}
	};
	const handleCreatePageButtonClick = (e: React.MouseEvent<HTMLElement>) => {
		fireAnalytics();
		const shouldCreate = shouldCreateBlankFabricPage();

		if (shouldCreate) {
			if (e.metaKey || e.ctrlKey) {
				window.open(createPageHref, '_blank', 'noreferrer');
			} else {
				push(createPageHref);
			}
		} else {
			// creation is handled by href for button click
			e.preventDefault();
		}
	};

	const createBlog = () => {
		const createBlogLink = buildCreationLink({
			contentType: 'blog',
			source: 'globalCreateBlogShortcut',
			spaceKey,
		});
		push(createBlogLink);
	};

	const createLiveDoc = () => {
		const createLiveDocLink = buildCreationLink({
			contentType: 'page',
			subType: ConfluencePageSubType.LIVE,
			parentPageId: contentId,
			withFallback: true,
			source: 'globalCreateLiveDocShortcut',
			spaceKey,
		});
		push(createLiveDocLink);
	};

	const showLiveDocNotEnabledFlag = () => {
		void flags.showErrorFlag({
			id: 'live-doc-not-enabled-shortcut-flag',
			title: <FormattedMessage {...i18n.liveDocsNotEnabledTitle} />,
			description: <FormattedMessage {...i18n.liveDocsNotEnabledDescription} />,
			isAutoDismiss: true,
			actions: [
				{
					content: <FormattedMessage {...i18n.liveDocsNotEnabledAction} />,
					onClick: () => {
						window.open(LIVE_DOCS_LINK, '_blank');
					},
				},
			],
		});
	};

	const [createPageClickListener, createPageShortcutListener] = isCreateBlankFabricPageEnabled()
		? [handleCreatePageButtonClick, handleCreatePageShortcut]
		: [openCreateDialog, openCreateDialog];

	const universalGlobalCreateShortcut = isMac()
		? UNIVERSAL_GLOBAL_CREATE_SHORTCUT_MAC
		: UNIVERSAL_GLOBAL_CREATE_SHORTCUT_WIN;

	const handlePreload = () => {
		// preload smart creation
		void ConfluenceSmartCreationModalLoader.preload();
	};

	return (
		// disabling this for now, will move preloading to trigger button when it supports onMouseEnter
		<div
			data-testid="app-navigation-create"
			data-pendo-id="con-*/wiki/home-create"
			data-vc="create-component"
			{...ssrPlaceholderIdProp}
			css={containerStyles}
			onMouseEnter={handlePreload}
			onFocus={handlePreload}
		>
			<EngagementInlineDialogLoader
				engagementId="createComponentButton"
				dataVC="app-navigation-create-engagement-inline-dialog"
			>
				{isCreationDropdownEnabled ? (
					<UniversalCreate />
				) : (
					<Tooltip
						content={
							areKeyboardShortcutsEnabled && (
								<ShortcutVisualizer shortcut={universalGlobalCreateShortcut} isTooltip />
							)
						}
					>
						<CreateContentButtonPreloader editSource={CREATE_NAV_BUTTON_CLICK}>
							<CreateButton onClick={enforceStorageLimit(createPageClickListener)}>
								<FormattedMessage {...i18n.create} />
							</CreateButton>
						</CreateContentButtonPreloader>
					</Tooltip>
				)}
			</EngagementInlineDialogLoader>
			<GeneralShortcutListener
				deprecatedAccelerator={
					!(isLiveEditMode || isOnEditRoute) ? GLOBAL_CREATE_SHORTCUT : undefined
				}
				accelerator={universalGlobalCreateShortcut}
				listener={enforceStorageLimit(createPageShortcutListener)}
				isAppNavigationShortcut
			/>
			<GeneralShortcutListener
				deprecatedAccelerator={!(isLiveEditMode || isOnEditRoute) ? BLOG_SHORTCUT : undefined}
				accelerator={UNIVERSAL_BLOG_SHORTCUT}
				listener={enforceStorageLimit(createBlog)}
				isAppNavigationShortcut
			/>
			{isCreateLiveDocEnabled ? (
				<GeneralShortcutListener
					accelerator={UNIVERSAL_CREATE_LIVE_DOC_SHORTCUT}
					listener={enforceStorageLimit(createLiveDoc)}
					isAppNavigationShortcut
				/>
			) : (
				<GeneralShortcutListener
					accelerator={UNIVERSAL_CREATE_LIVE_DOC_SHORTCUT}
					listener={enforceStorageLimit(showLiveDocNotEnabledFlag)}
					isAppNavigationShortcut
				/>
			)}
		</div>
	);
};

const i18n = defineMessages({
	create: {
		id: 'app-navigation.top-navigation.create',
		defaultMessage: 'Create',
		description:
			'Label for the Create button in the top navigation that opens the content creation menu',
	},
	liveDocsNotEnabledTitle: {
		id: 'app-navigation.shortcuts.flags.liveDocsNotEnabled.title',
		description:
			'Title for the flag that informs the user that live docs are not enabled in this space when using the create shortcut',
		defaultMessage: 'Live docs aren’t enabled in this space',
	},
	liveDocsNotEnabledDescription: {
		id: 'app-navigation.shortcuts.flags.liveDocsNotEnabled.description',
		description:
			'Description for the flag that informs the user that live docs are not enabled in this space when using the create shortcut',
		defaultMessage: 'To use this shortcut, your space admin will need to enable live docs.',
	},
	liveDocsNotEnabledAction: {
		id: 'app-navigation.shortcuts.flags.liveDocsNotEnabled.action',
		description:
			'Live Docs Documentation Link for the flag that informs the user that live docs are not enabled in this space when using the create shortcut',
		defaultMessage: 'Find out more',
	},
});

export const CreateComponent: React.ComponentType<
	Omit<
		{
			flags: any;
		},
		'flags'
	>
> = withFlags(memo(CreateComponentContent));
